import { inputCls } from "./Field";

/**
 * Inter-company Transfer dialog — shown when an admin changes the Company
 * field on an existing employee. Captures the legal names and effective date,
 * then the parent calls /api/employees/{id}/transfer to persist + generate
 * the PDF letter.
 */
export default function TransferDialog({
    employee, form, transferData, setTransferData, transferring,
    onCancel, onConfirm,
}) {
    return (
        <div
            className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm flex items-center justify-center px-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="transfer-dialog"
        >
            <div className="bg-white border border-ink/10 w-full max-w-lg shadow-2xl">
                <div className="px-7 py-5 border-b border-ink/10 bg-klein/5">
                    <div className="overline text-klein">Inter-company transfer</div>
                    <h3 className="font-display font-bold text-xl mt-1 leading-tight">
                        Generate transfer letter
                    </h3>
                    <p className="text-xs text-ink/60 mt-1.5 leading-relaxed">
                        You changed <b>{employee?.company || "—"}</b> → <b>{form.company}</b>. Provide the
                        new company's <b>legal name</b> and effective date — we'll save the change, create
                        the letter PDF, and attach it to the employee's documents.
                    </p>
                </div>
                <div className="px-7 py-5 space-y-4">
                    <div>
                        <label className="overline block mb-2">From company legal name</label>
                        <input
                            value={transferData.from_company_legal_name}
                            onChange={(e) => setTransferData({ ...transferData, from_company_legal_name: e.target.value })}
                            className={inputCls}
                            placeholder={`e.g. ${employee?.company || "Previous"} Limited`}
                            data-testid="transfer-from-legal-name-input"
                        />
                        <p className="text-[11px] text-ink/40 mt-1.5">
                            Optional — defaults to the previous trade name (<b>{employee?.company || "—"}</b>) if left blank.
                        </p>
                    </div>
                    <div>
                        <label className="overline block mb-2">New company legal name <span className="text-destructive">*</span></label>
                        <input
                            value={transferData.new_company_legal_name}
                            onChange={(e) => setTransferData({ ...transferData, new_company_legal_name: e.target.value })}
                            className={inputCls}
                            placeholder={`e.g. ${form.company} Investments Limited`}
                            autoFocus
                            data-testid="transfer-legal-name-input"
                        />
                        <p className="text-[11px] text-ink/40 mt-1.5">
                            Full registered name as on CAC docs — appears verbatim on the letter.
                        </p>
                    </div>
                    <div>
                        <label className="overline block mb-2">Effective date</label>
                        <input
                            type="date"
                            value={transferData.effective_date}
                            onChange={(e) => setTransferData({ ...transferData, effective_date: e.target.value })}
                            className={inputCls}
                            data-testid="transfer-effective-date"
                        />
                    </div>
                </div>
                <div className="px-7 py-4 border-t border-ink/10 flex items-center justify-end gap-3 bg-bone/30">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={transferring}
                        className="px-4 py-2.5 border border-ink/20 text-ink text-sm hover:bg-ink/5 disabled:opacity-60"
                        data-testid="transfer-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={transferring || !transferData.new_company_legal_name.trim()}
                        className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60"
                        data-testid="transfer-confirm"
                    >
                        {transferring ? "Generating…" : "Save & generate letter"}
                    </button>
                </div>
            </div>
        </div>
    );
}
